import { useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client/react';
import { useRouter } from '@tanstack/react-router';
import cookie from 'js-cookie';
import { getGclid } from '@sc/shared-ui';
import { GQL_MUTATION_AUTHORISE_WITH_AUTHORISATION_CDOE } from '../utility/queries';
import { trackSignUpConversion } from '../utility/trackSignUpConversion';
import { track } from '../utility/analytics';
import { useAlerts } from '../providers/alerts';
import { useAuthenticated } from '../providers/authenticated';

// AuthButtons stashed the PKCE verifier against the state string
export const useOAuthCallback = ({
  code,
  state,
  onCompleted,
  onFailed,
}: {
  code: string | undefined;
  state: object | string | undefined;
  onCompleted: (registered: boolean) => void;
  onFailed: () => void;
}) => {
  const router = useRouter();

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const { setIsAuthenticated } = useAuthenticated();

  const [authoriseWithCode, { loading: isAuthorising }] = useMutation(
    GQL_MUTATION_AUTHORISE_WITH_AUTHORISATION_CDOE,
    {
      onCompleted: (data) => {
        const { registeredCompanyUuid } = data.authoriseWithAuthorisationCode;

        if (registeredCompanyUuid) {
          trackSignUpConversion(registeredCompanyUuid);

          track('Sign Up Completed', { method: 'google' });
        } else {
          track('Login Completed', { method: 'google' });
        }

        const authDate = new Date().getTime().toString();

        setIsAuthenticated(authDate);

        router.options.context.isAuthenticated = authDate;

        onCompleted(!!registeredCompanyUuid);
      },
      onError: (err) => {
        onError(err);

        onFailed();
      },
    }
  );

  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;

    if (!code || typeof state !== 'string') return;

    handled.current = true;

    const decodedState = decodeURIComponent(state);

    const codeVerifier = cookie.get(`codeVerifier-${decodedState}`);

    if (!codeVerifier) throw new Error('Invalid code verifier');

    cookie.remove(`codeVerifier-${decodedState}`);

    void authoriseWithCode({
      variables: {
        input: {
          code,
          state: decodedState,
          verifier: codeVerifier,
          gclid: getGclid(),
        },
      },
    });
  }, [code, state, authoriseWithCode]);

  return { isAuthorising };
};
